import { useCallback, useEffect, useRef, useState } from 'react';

import { useCollegamentoProfilo } from '@/hooks/useCollegamentoProfilo';
import { serveCrearloProfilo, useProfiloPerSfida } from '@/hooks/useProfiloPerSfida';
import { useSyncProfiloLimulo } from '@/hooks/useSyncProfiloLimulo';
import { challengeReasonOf, limuloFailure, profileReasonOf } from '@/lib/limulo/errors';
import {
  esitoDaSyncAlTocco,
  esitoProfiloNonCollegato,
  profileIdDellaSfida,
  rifiutoSfidaOf,
  type EsitoRifiutoSfida,
} from '@/lib/limulo/challenge';
import { attesaProfili, esitoSyncDi, profiloPerIscrizione, type AttesaProfili } from '@/lib/limulo/registration';
import { useCreateLimuloChallengeMutation, useGetLimuloProfilesQuery } from '@/services/limuloApi';

/**
 * **L'invio della sfida di piattaforma** (I-207, lotto invio, contratto PR169 fusa `f0db426`;
 * dal lotto C1 di T2-3 vale per ogni gioco, backgammon compreso): `POST /v1/challenges`, sempre
 * nominativa.
 *
 * Mutazioni fuori da `SfidaSheet.tsx` (il pannello): il pannello riceve solo lo stato e le
 * callback (`tools/verify-sfide.ts`, guardia 4).
 *
 * Il `profileId` si prende dalla stessa fonte e con la stessa regola dell'iscrizione ai tornei
 * (`profiloPerIscrizione`, `lib/limulo/registration.ts`): un profilo attivo per il gioco ⇒ si
 * manda quello; con piu' di uno si manda il PRIMO (qui non c'e' un selettore — la scelta fra piu'
 * profili per lo stesso gioco e' una rifinitura fuori dallo scopo di questo lotto, in APERTO).
 *
 * 🔴 **BLOCCANTE, corretto in revisione (sfide-orcr, 28/9): senza una lista ARRIVATA non si manda
 * `profileId: null`.** Prima di questa correzione, con la lista dei profili ancora in lettura o in
 * errore, `invia` mandava comunque `profileId: null` — il motore lo legge come «non hai un
 * profilo» (`sfida.profilo-richiesto`, `sfide.js:191-192`) anche a chi ce l'ha, esattamente il
 * falso che `attesaProfili` (usata da `LimuloRegistrationActions` sull'iscrizione ai tornei) esiste
 * per evitare. Qui la stessa regola: `statoProfili(gameId)` dice al chiamante se aspettare o
 * offrire «riprova» (`illeggibili` ⇒ `riprovaProfili`), e `invia` stesso si RIFIUTA di partire se
 * lo stato non e' `pronti` — non solo il bottone spento a schermo, una guardia in piu' dentro
 * l'hook (stessa lezione della guardia anti-doppio-tap qui sotto: un bottone disabilitato non e'
 * un cancello, e' un suggerimento).
 */

export type StatoInvioSfida = 'scelta' | 'inviando' | 'inviata' | 'errore';

export interface InvioSfidaPiattaforma {
  stato: StatoInvioSfida;
  /** Popolato solo quando `stato === 'errore'`. */
  esito: EsitoRifiutoSfida | null;
  /** kb#388: `invia` sta aspettando il sync del profilo (e `stato` e' `inviando`): il pannello dice «Sto collegando il tuo profilo…». */
  collegando: boolean;
  /**
   * Lo stato della lista profili PER il gioco `gameId` (stessa regola dell'iscrizione ai tornei,
   * `attesaProfili`): `in-lettura`/`illeggibili` ⇒ il chiamante aspetta o offre `riprovaProfili`.
   * `invia` per quel `gameId` non parte finche' non torna `pronti`.
   */
  statoProfili: (gameId: string) => AttesaProfili;
  /** Richiede di nuovo la lista dei profili (dopo un errore). */
  riprovaProfili: () => void;
  /**
   * Il gioco che il pannello ha SCELTO (`''` = nessuno): e' cio' per cui si sincronizza il profilo
   * (I-493). Il pannello lo dice, qui si decide cosa farne: il sync sta negli hook, non nel pannello.
   */
  scegliGioco: (gameId: string) => void;
  invia: (input: { gameId: string; gameModeKey: string }) => void;
  /** Torna a `scelta`, senza toccare l'invito: usato quando si cambia gioco dopo un esito. */
  reset: () => void;
}

export function useInvioSfidaPiattaforma(amicoId: string): InvioSfidaPiattaforma {
  const profiliQuery = useGetLimuloProfilesQuery(undefined);
  const [creaSfida] = useCreateLimuloChallengeMutation();
  const [stato, setStato] = useState<StatoInvioSfida>('scelta');
  const [esito, setEsito] = useState<EsitoRifiutoSfida | null>(null);
  const [giocoScelto, setGiocoScelto] = useState('');
  // I-493: chi sfida da un gioco in cui non e' mai passato da una scheda torneo ha un profilo senza `sub`
  // del provider, e la sfida e' rifiutata (`sub_missing`). STESSO hook e STESSA regola delle schede
  // (una volta per profilo e per sessione, solo schemi a zero campi, fire-and-forget): `invia` non lo aspetta.
  useSyncProfiloLimulo(giocoScelto);
  /**
   * ⚠️ La guardia contro il doppio `invia` nello stesso turno sta in una REF, NON in `stato`:
   * `setStato('inviando')` non ha effetto sulla riga successiva (React batcha l'aggiornamento), e
   * due chiamate nello stesso tick leggerebbero entrambe lo `stato` VECCHIO — partirebbero
   * entrambe. Stessa difesa di `LimuloRegistrationActions.inCorso` sull'iscrizione ai tornei.
   */
  const inVolo = useRef(false);
  /**
   * kb#388: il foglio che si CHIUDE mentre `invia` aspetta il sync (fino a 8 s, o la creazione del profilo) smonta l'hook: la
   * sfida NON deve partire da un foglio che non c'e' piu', senza un esito che nessuno vedrebbe. Chiusura durante l'attesa =
   * la Sfida non parte. (Tutti i punti di montaggio del foglio sono `{aperto && <SfidaSheetDaCatalogo />}`: chiudere = smontare.)
   */
  const montato = useRef(true);
  useEffect(() => {
    montato.current = true;
    return () => {
      montato.current = false;
    };
  }, []);
  // I-543: il profilo che manca si crea al tocco di Invia, se lo schema del gioco non chiede niente.
  const preparaProfilo = useProfiloPerSfida();
  // kb#388: col profilo gia' li', `invia` aspetta il sync in volo (o rifatto, se era caduto) prima di mandare.
  const { collega, collegando } = useCollegamentoProfilo();

  const statoProfili = useCallback(
    (gameId: string): AttesaProfili =>
      attesaProfili(profiloPerIscrizione(profiliQuery.data, gameId), profiliQuery.isError),
    [profiliQuery.data, profiliQuery.isError],
  );

  const riprovaProfili = useCallback(() => {
    void profiliQuery.refetch();
  }, [profiliQuery]);

  const invia = useCallback(
    ({ gameId, gameModeKey }: { gameId: string; gameModeKey: string }) => {
      if (inVolo.current) return;
      const scelta = profiloPerIscrizione(profiliQuery.data, gameId);
      if (attesaProfili(scelta, profiliQuery.isError) !== 'pronti') return;
      inVolo.current = true;
      setStato('inviando');
      setEsito(null);
      const manda = (profileId: string | null) =>
        creaSfida({ gameId, gameModeKey, targetSub: amicoId, profileId })
          .unwrap()
          .then(() => {
            inVolo.current = false;
            setStato('inviata');
          })
          .catch((e: unknown) => {
            inVolo.current = false;
            const failure = limuloFailure(e);
            // kb#388: `profile.not-linked` letto con l'ultimo esito del sync di QUEL profilo ⇒ la frase col gesto vero.
            const nonCollegato = esitoProfiloNonCollegato(
              profileReasonOf(e),
              profileId === null ? undefined : esitoSyncDi(profileId),
            );
            setEsito(nonCollegato ?? rifiutoSfidaOf(challengeReasonOf(e), failure.code ?? String(failure.status ?? '')));
            setStato('errore');
          });
      // Un profilo c'e' (o il gioco non ce l'ha): come prima, SUBITO — salvo un sync del suo profilo in volo o caduto (kb#388):
      // allora il tocco lo aspetta (uno solo) e la sfida parte solo se e' andato.
      if (!serveCrearloProfilo(scelta, gameId)) {
        const profileId = profileIdDellaSfida(scelta);
        collega(profileId, (collegamento) => {
          if (!montato.current) {
            inVolo.current = false;
            return;
          }
          if (collegamento.kind === 'ok') {
            void manda(profileId);
            return;
          }
          inVolo.current = false;
          setEsito(esitoDaSyncAlTocco(collegamento));
          setStato('errore');
        });
        return;
      }
      // I-543: nessun profilo per il gioco. Se lo schema non chiede niente il profilo nasce ORA, al tocco di Invia, e la
      // sfida parte col suo id; con campi parte com'e' sempre (il motore dira' `profilo-richiesto`, con il suo link).
      // `inVolo` e' gia' vero: un secondo `invia` mentre si crea il profilo esce alla prima riga.
      void preparaProfilo(gameId, scelta).then((profilo) => {
        if (!montato.current) {
          inVolo.current = false;
          return undefined;
        }
        if (profilo.kind === 'pronto') return manda(profilo.profileId);
        // ⛔ MAI una sfida senza il profilo che si doveva creare.
        inVolo.current = false;
        setEsito(
          profilo.kind === 'non-creato'
            ? { kind: 'profilo', chiave: 'social.sfida_invio_profilo_non_creato', ragione: profilo.ragione }
            : profilo.kind === 'senza-id'
              ? { kind: 'profilo', chiave: 'social.sfida_invio_profilo_senza_id' }
              : profilo.kind === 'collegamento'
                ? esitoDaSyncAlTocco(profilo.esito)
                : { kind: 'profilo', chiave: 'social.sfida_profili_illeggibili' },
        );
        setStato('errore');
      });
    },
    [amicoId, collega, creaSfida, preparaProfilo, profiliQuery.data, profiliQuery.isError],
  );

  const reset = useCallback(() => {
    setStato('scelta');
    setEsito(null);
  }, []);

  return { stato, esito, collegando, statoProfili, riprovaProfili, scegliGioco: setGiocoScelto, invia, reset };
}
